import { UseQueryResult } from '@tanstack/react-query'
import {
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { MdFormatSize, MdRedo, MdUndo } from 'react-icons/md'
import { useTranslation } from 'react-i18next'
import Paywall from '../Components/Paywall'
import { SheetType } from '../Components/Sheets/lib/Enums/SheetType'
import { useSheet, useUpdateSheet } from '../Components/Sheets/lib/Hooks/Sheet'
import { isPaymentOverdue } from '../Components/Sheets/lib/Payment'
import SheetTitle from '../Components/Sheets/SheetTitle'
import CellStylingColorPicker from '../Components/Sheets/Spreadsheet/CellStylingColorPicker'
import CellStyling from '../Components/Sheets/Spreadsheet/CellStylingToggle'
import SpreadsheetMenu from '../Components/Sheets/Spreadsheet/SpreadsheetMenu'
import TrialEndedPaywallContent from '../Components/Sheets/TrialEndedPaywallContent'
import WelcomeSpreadsheet from '../Components/Sheets/WelcomeSpreadsheet'
import SpreadsheetFormulaCalculator, {
  SpreadsheetFormulaError,
} from '../lib/SpreadsheetFormulaCalculator'
import { useFormatCurrency } from '../lib/CurrencyFormatter'
import {
  SpreadsheetSnapshot,
  useSpreadsheetHistory,
} from '../lib/Hooks/useSpreadsheetHistory'
import { Cell, CellType, Sheet, Spreadsheet, Style } from '../lib/Types'
import './SpreadSheetPage.css'

const MIN_COLUMN_WIDTH = 40
const MIN_ROW_HEIGHT = 24
// Matches the `gap-3` (0.75rem) Tailwind class between the formatting
// toolbar and the formula bar input in the row below.
const FORMULA_BAR_GAP = 12

const REFERENCE_COLOR_PALETTE: { bg: string; text: string }[] = [
  { bg: 'bg-red-100', text: 'text-red-300' },
  { bg: 'bg-blue-100', text: 'text-blue-300' },
  { bg: 'bg-green-100', text: 'text-green-300' },
  { bg: 'bg-yellow-100', text: 'text-yellow-300' },
  { bg: 'bg-purple-100', text: 'text-purple-300' },
  { bg: 'bg-orange-100', text: 'text-orange-300' },
]

export default function SpreadSheetPage() {
  const { t } = useTranslation('spreadsheet')
  const urlParams = new URLSearchParams(window.location.search)
  const { data: spreadsheet } = useSheet() as UseQueryResult<Spreadsheet>
  const { mutate: updateSpreadsheet } = useUpdateSheet()
  const formatCurrency = useFormatCurrency()
  const spreadsheetHistory = useSpreadsheetHistory(spreadsheet?.id)

  // Blocks formatting/editing actions once the free trial has expired,
  // showing the "trial ended" paywall instead of running the action.
  const [showTrialEndedPaywall, toggleTrialEndedPaywall] = useState(false)
  const blockedByTrial = (): boolean => {
    if (!isPaymentOverdue(spreadsheet!)) return false
    toggleTrialEndedPaywall(true)
    return true
  }

  const [highlightedCell, setHighLightedCell] = useState<string>('')
  // The far corner of a multi-cell selection (drag or shift+arrow). Empty
  // when the selection is just the single `highlightedCell`.
  const [selectionEnd, setSelectionEnd] = useState<string>('')
  // Individual cells added via Cmd/Ctrl+click, on top of the anchor/selectionEnd
  // rectangle — lets the selection include cells outside that rectangle.
  const [additionalSelectedCells, setAdditionalSelectedCells] = useState<
    Set<string>
  >(new Set())
  const [activatedCell, setActivatedCell] = useState<string>('')
  const [activatedCellContent, setActivatedCellContent] = useState<string>('')
  const [cellErrors, setCellErrors] = useState<Map<string, string>>(new Map())
  const activeTextareaRef = useRef<HTMLTextAreaElement | null>(null)
  // The formula-highlight overlay lays out with real text metrics (unlike a
  // textarea, whose CSS width doesn't track its value), so its measured
  // width drives the textarea's width to keep the native caret in sync.
  const activeOverlayRef = useRef<HTMLDivElement | null>(null)
  const [activatedCellTextWidth, setActivatedCellTextWidth] = useState(0)
  // Grid content div shrink-wraps to the sum of the column widths, so its
  // measured width is the table's actual rendered width, columns resized or not.
  const gridContentRef = useRef<HTMLDivElement | null>(null)
  // This row's own box always equals the page's available content width
  // (a block box isn't stretched by an overflowing child), so it's the
  // "page width" side of the width comparison below.
  const formulaBarRowRef = useRef<HTMLDivElement | null>(null)
  // The formula bar sits to the right of this toolbar in the same row, so its
  // own width must exclude the toolbar's width to land its right edge on the
  // table's right edge rather than that far past it.
  const formattingToolbarRef = useRef<HTMLDivElement | null>(null)
  const [formulaBarWidth, setFormulaBarWidth] = useState<number>()
  // Set on mousedown (which fires before blur) when the click is about to
  // insert a cell reference, so onBlur can tell that apart from a real
  // commit (Enter, clicking away) even though both can leave the caret
  // sitting right after an operator/paren/colon.
  const referenceInsertPendingRef = useRef(false)
  // Set on mousedown of the formula bar input (which fires before the
  // in-cell textarea's blur) so onBlur can tell that the focus is merely
  // moving to the formula bar, rather than a real commit, and skip saving.
  const formulaBarFocusPendingRef = useRef(false)
  // Set right before a programmatic blur() triggered by Escape (in either the
  // in-cell textarea or the formula bar input), so the corresponding onBlur
  // handler cancels the edit — reverting to `highlightedCell` — instead of
  // saving it like a normal commit/focus-shift blur would.
  const escapeCancelPendingRef = useRef(false)
  // Set right before activating a cell from the formula bar (typing while a
  // cell is only highlighted), so the textarea-focus effect below knows to
  // leave focus in the formula bar instead of stealing it.
  const skipTextareaAutoFocusRef = useRef(false)
  // The cell a mousedown started on, while the button is still held — drives
  // drag-to-select via onMouseEnter. Null when not dragging.
  const mouseDownCellRef = useRef<string | null>(null)
  // Set once a drag crosses into a second cell, so the click event that
  // follows mouseup (fires when the drag ends back on its own start cell)
  // can be told apart from a real, un-dragged click.
  const didDragRef = useRef(false)

  const [hoveredRow, setHoveredRow] = useState<number | null>(null)

  const [resizeConfiguration, setResizeConfiguration] = useState<
    { startWidth: number; row: string; x: number; width?: number } | undefined
  >()

  const [rowResizeConfiguration, setRowResizeConfiguration] = useState<
    | { startHeight: number; row: number; y: number; height?: number }
    | undefined
  >()

  const columnCodesCache = new Map()

  const getColumnCode = (column: number): string => {
    if (columnCodesCache.get(column)) return columnCodesCache.get(column)

    // If there are more columns than letters in the english alphabet, calulate the prefix letter (a, b, c, etc).
    const prefixLetterNumber = Math.floor(column / 26)

    const letterIndex = column - prefixLetterNumber * 26

    const code = String.fromCharCode(97 + letterIndex)

    const codeToReturn =
      prefixLetterNumber > 0
        ? `${getColumnCode(prefixLetterNumber - 1)}${code}`
        : code

    columnCodesCache.set(column, codeToReturn)

    return codeToReturn
  }
  const getColumnIndex = (code: string): number => {
    if (code.length === 1) return code.charCodeAt(0) - 97
    const lastChar = code.charCodeAt(code.length - 1) - 97
    return (getColumnIndex(code.slice(0, code.length - 1)) + 1) * 26 + lastChar
  }

  const parseCellId = (cellId: string): { column: number; row: number } => {
    const match = cellId.match(/^([a-z]+)(\d+)$/)!
    return { column: getColumnIndex(match[1]), row: parseInt(match[2]) - 1 }
  }

  const getCellId = (column: number, row: number) => {
    return `${getColumnCode(column)}${row + 1}`
  }

  // The rectangle spanned by `highlightedCell` (anchor) and `selectionEnd`
  // (far corner) — a single cell when there's no active multi-cell selection.
  const getSelectionBounds = (): {
    minColumn: number
    maxColumn: number
    minRow: number
    maxRow: number
  } | null => {
    if (!highlightedCell) return null

    const endCellId = selectionEnd || highlightedCell
    const start = parseCellId(highlightedCell)
    const end = parseCellId(endCellId)

    return {
      minColumn: Math.min(start.column, end.column),
      maxColumn: Math.max(start.column, end.column),
      minRow: Math.min(start.row, end.row),
      maxRow: Math.max(start.row, end.row),
    }
  }

  const getSelectedCellIds = (): Set<string> => {
    const bounds = getSelectionBounds()
    const ids = new Set<string>(additionalSelectedCells)

    if (bounds) {
      for (let row = bounds.minRow; row <= bounds.maxRow; row++) {
        for (let column = bounds.minColumn; column <= bounds.maxColumn; column++) {
          ids.add(getCellId(column, row))
        }
      }
    }
    return ids
  }

  // The bottom-right-most cell across the whole selection (rectangle plus any
  // Cmd/Ctrl-added cells) — used to anchor the sum/average label, since that
  // can now be outside the anchor/selectionEnd rectangle.
  const getOverallSelectionBounds = (
    ids: Set<string>,
  ): { maxColumn: number; maxRow: number } | null => {
    if (ids.size === 0) return null

    let maxColumn = -Infinity
    let maxRow = -Infinity
    ids.forEach((cellId) => {
      const { column, row } = parseCellId(cellId)
      if (column > maxColumn) maxColumn = column
      if (row > maxRow) maxRow = row
    })
    return { maxColumn, maxRow }
  }

  // Sum/average of the numeric (normal or currency) cells within a multi-cell
  // selection — null when the selection is a single cell, none of its cells
  // hold summable content, or any non-empty cell holds non-numeric content
  // (a single text cell like "Hej2+" invalidates the whole selection, rather
  // than being silently skipped).
  const getSelectionSummary = (
    selectedIds: Set<string>,
  ): { total: number; average: number; isCurrency: boolean } | null => {
    if (selectedIds.size <= 1) return null

    let total = 0
    let count = 0
    let isCurrency = false

    for (const cellId of selectedIds) {
      const cell = spreadsheet!.data.get(cellId)
      if (!cell || cell.value === '') continue

      const numericValue = Number(cell.value)
      if (!Number.isFinite(numericValue)) return null

      total += numericValue
      count += 1
      if (cell.type === 'currency') isCurrency = true
    }

    if (count === 0) return null

    return { total, average: total / count, isCurrency }
  }

  const getCellValue = (cellId: string) => {
    const cellContent = spreadsheet!.data.get(cellId)
    if (!cellContent) return ''

    if (cellContent.type === 'currency') {
      if (cellContent.value === '') return ''

      // Computed values are meant to always be stored in canonical '.'-decimal
      // form (see SpreadsheetFormulaCalculator), but formulas only ever
      // re-run when a cell is directly re-saved — never on load — so a cell
      // last computed before that invariant held can still have a decimal
      // comma baked into the stored value (from whatever language was active
      // when it was saved, not necessarily the current session's). Falling
      // back to a straight comma->dot swap recovers those without needing
      // every affected cell to be re-edited — deliberately not using the
      // current locale's separator, since that's unrelated to the language
      // active when the stale value was written.
      let numericValue = Number(cellContent.value)
      if (
        !Number.isFinite(numericValue) &&
        cellContent.value.includes(',') &&
        !cellContent.value.includes('.')
      ) {
        numericValue = Number(cellContent.value.replace(',', '.'))
      }

      if (Number.isFinite(numericValue)) {
        return formatCurrency(numericValue)
      }

      console.warn(
        `[Spreadsheet] Cell ${cellId} is formatted as currency but its value could not be converted to a number.`,
        {
          cellId,
          content: cellContent.content,
          value: cellContent.value,
          numericValue,
        },
      )
    }

    return SpreadsheetFormulaCalculator.formatForDisplay(cellContent.value)
  }

  const getCellType = (cellId: string): CellType =>
    spreadsheet!.data.get(cellId)?.type ?? 'normal'

  const getCellContent = (cellId: string) => {
    const cellContent = spreadsheet!.data.get(cellId)

    return cellContent ? cellContent.content : ''
  }

  const getCellClassNames = (cellId: string) => {
    const cell = spreadsheet!.data.get(cellId)

    return cell && cell.styling ? cell.styling.join(' ') : ''
  }

  const captureSnapshot = (): SpreadsheetSnapshot => ({
    data: Array.from(spreadsheet!.data.entries()),
    rows: Array.from(spreadsheet!.rows.entries()),
    number_of_columns: spreadsheet!.number_of_columns,
    number_of_rows: spreadsheet!.number_of_rows,
  })

  const applySnapshot = (snapshot: SpreadsheetSnapshot) => {
    updateSpreadsheet({
      ...spreadsheet,
      data: new Map(snapshot.data),
      rows: new Map(snapshot.rows),
      number_of_columns: snapshot.number_of_columns,
      number_of_rows: snapshot.number_of_rows,
    } as Sheet)
  }

  // Every data/rows/dimension change that should be undoable goes through
  // this instead of `updateSpreadsheet` directly, so it always records the
  // pre-change state first.
  //
  // The "previous" state is read from the history's own tracked `current`
  // rather than freshly captured from `spreadsheet` — that React Query cache
  // only catches up once an earlier undo/redo's mutation resolves, so a
  // change made right after one (before that lands) would otherwise record
  // the wrong "previous" state and desync the stacks from the screen.
  const updateSpreadsheetTracked = (change: Partial<Spreadsheet>) => {
    const previous = spreadsheetHistory.current ?? captureSnapshot()
    const next: SpreadsheetSnapshot = {
      data: change.data ? Array.from(change.data.entries()) : previous.data,
      rows: change.rows ? Array.from(change.rows.entries()) : previous.rows,
      number_of_columns:
        change.number_of_columns ?? previous.number_of_columns,
      number_of_rows: change.number_of_rows ?? previous.number_of_rows,
    }
    spreadsheetHistory.recordSnapshot(previous, next)
    updateSpreadsheet({ ...spreadsheet, ...change } as Sheet)
  }

  const undo = () => {
    if (blockedByTrial()) return
    const previous = spreadsheetHistory.undo()
    if (previous) applySnapshot(previous)
  }

  const redo = () => {
    if (blockedByTrial()) return
    const next = spreadsheetHistory.redo()
    if (next) applySnapshot(next)
  }

  const renderHighlightedFormula = (
    content: string,
    referenceColors: Map<string, { bg: string; text: string }>,
  ) =>
    content
      .split(/([a-zA-Z]+[0-9]+)/g)
      .map((part, index) => {
        const color = referenceColors.get(part.toLowerCase())

        return color
          ? (
            <span key={index} className={`bg-white ${color.text}`}>
              {part}
            </span>
          )
          : (
            <span key={index} className='bg-white'>
              {part}
            </span>
          )
      })

  const highlightCell = (cellId: string) => {
    if (highlightedCell === cellId) {
      setHighLightedCell('')
    } else {
      setHighLightedCell(cellId)
    }
    setSelectionEnd('')
    setAdditionalSelectedCells(new Set())
    setActivatedCell('')
  }

  const activateCell = (cellId: string) => {
    if (blockedByTrial()) return
    setActivatedCellContent(getCellContent(cellId))
    setActivatedCell(cellId)
    setHighLightedCell('')
    setSelectionEnd('')
    setAdditionalSelectedCells(new Set())
  }

  // Typing directly on a highlighted (not yet activated) cell starts a brand
  // new value, discarding whatever was there — matching Excel/Sheets, where
  // Enter/F2/double-click open the existing content for editing but typing
  // a character replaces it outright.
  const activateCellWithText = (cellId: string, text: string) => {
    if (blockedByTrial()) return
    setActivatedCellContent(text)
    setActivatedCell(cellId)
    setHighLightedCell('')
    setSelectionEnd('')
    setAdditionalSelectedCells(new Set())
  }

  const saveCellContent = (cellId: string) => {
    const formulaCalculator = new SpreadsheetFormulaCalculator(
      spreadsheet!.data,
    )

    const errors = formulaCalculator.errors

    try {
      formulaCalculator.rebuildSpreadsheet(cellId, activatedCellContent, 0)
    } catch (error) {
      if (error instanceof SpreadsheetFormulaError) {
        errors.set(cellId, error.message)
      } else {
        errors.set(cellId, t('spreadsheet:unknownError'))
      }
    }

    updateSpreadsheetTracked({
      data: formulaCalculator.getData(),
    })

    setActivatedCellContent('')
    setActivatedCell('')

    setCellErrors(errors)
  }

  const formulaBarCellId = activatedCell || highlightedCell

  const formulaBarValue = activatedCell
    ? activatedCellContent
    : highlightedCell
      ? getCellContent(highlightedCell)
      : ''

  const handleFormulaBarChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!formulaBarCellId) return

    if (!activatedCell) {
      if (blockedByTrial()) return
      skipTextareaAutoFocusRef.current = true
      setActivatedCell(formulaBarCellId)
      setHighLightedCell('')
      setSelectionEnd('')
      setAdditionalSelectedCells(new Set())
    }

    setActivatedCellContent(e.target.value)
  }

  const handleFormulaBarBlur = () => {
    if (escapeCancelPendingRef.current) {
      escapeCancelPendingRef.current = false
      return
    }
    if (activatedCell) {
      saveCellContent(activatedCell)
    }
  }

  const handleFormulaBarKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault()

      // Cancel the edit (discard unsaved changes) and fall back to just
      // highlighting the cell, rather than committing via the blur below.
      if (activatedCell) {
        escapeCancelPendingRef.current = true
        setActivatedCellContent('')
        setActivatedCell('')
        setHighLightedCell(activatedCell)
        setSelectionEnd('')
        setAdditionalSelectedCells(new Set())
      }
      e.currentTarget.blur()
      return
    }

    if (e.key !== 'Enter') return

    e.preventDefault()

    if (!formulaBarCellId) return

    // Saving happens in the blur handler below, triggered by this blur() call,
    // so Enter and click-away share a single save path.
    setHighLightedCell(formulaBarCellId)
    setSelectionEnd('')
    setAdditionalSelectedCells(new Set())
    e.currentTarget.blur()
  }

  useEffect(() => {
    if (!activatedCell) return

    if (skipTextareaAutoFocusRef.current) {
      skipTextareaAutoFocusRef.current = false
      return
    }

    const el = activeTextareaRef.current
    if (!el) return
    el.focus()
    // Land the caret after the content rather than wherever the browser's
    // default focus placement would put it — matters most right after
    // typing-to-replace, where the caret must sit after that first character.
    el.setSelectionRange(el.value.length, el.value.length)
  }, [activatedCell])

  useLayoutEffect(() => {
    if (!activatedCell) {
      setActivatedCellTextWidth(0)
      return
    }

    setActivatedCellTextWidth(
      activeOverlayRef.current?.getBoundingClientRect().width ?? 0,
    )
  }, [activatedCell, activatedCellContent])

  // Ends drag-to-select regardless of what element the mouseup lands on
  // (e.g. released outside the grid) — onMouseEnter checks this ref to know
  // whether a drag is in progress.
  useEffect(() => {
    const handleWindowMouseUp = () => {
      mouseDownCellRef.current = null
    }
    window.addEventListener('mouseup', handleWindowMouseUp)
    return () => window.removeEventListener('mouseup', handleWindowMouseUp)
  }, [])

  useEffect(() => {
    const gridEl = gridContentRef.current
    const rowEl = formulaBarRowRef.current
    if (!gridEl || !rowEl) return

    // Narrower table: line the formula bar's end up with the table's end.
    // Wider table (page scrolls horizontally): cap the formula bar at the
    // page's own width instead of growing it to match.
    const recompute = () => {
      const tableWidth = gridEl.getBoundingClientRect().width
      const pageWidth = rowEl.getBoundingClientRect().width
      const toolbarWidth =
        formattingToolbarRef.current?.getBoundingClientRect().width ?? 0

      setFormulaBarWidth(
        Math.max(
          0,
          Math.min(tableWidth, pageWidth) - toolbarWidth - FORMULA_BAR_GAP,
        ),
      )
    }

    const observer = new ResizeObserver(recompute)
    observer.observe(gridEl)
    observer.observe(rowEl)
    if (formattingToolbarRef.current) {
      observer.observe(formattingToolbarRef.current)
    }

    return () => observer.disconnect()
  }, [])

  const getRowWidth = (row: string): number => {
    // If current row is being resized
    if (
      resizeConfiguration &&
      resizeConfiguration.row === row &&
      typeof resizeConfiguration.width !== 'undefined'
    ) {
      return resizeConfiguration.width
    }
    if (spreadsheet!.rows.get(row)?.width) {
      return spreadsheet!.rows.get(row)!.width!
    }
    // The default row with is 96
    return 96
  }

  // Row height is undefined (auto) unless the user has explicitly resized it,
  // so unresized rows keep their current content-driven height.
  const getRowHeight = (row: number): number | undefined => {
    if (
      rowResizeConfiguration &&
      rowResizeConfiguration.row === row &&
      typeof rowResizeConfiguration.height !== 'undefined'
    ) {
      return rowResizeConfiguration.height
    }
    return spreadsheet!.rows.get(String(row))?.height
  }

  // Unresized rows have no stored height (they auto-size to content), so a
  // drag needs the row's current on-screen height as its starting point.
  const measureRowHeight = (target: HTMLElement, row: number): number => {
    const stored = getRowHeight(row)
    if (typeof stored !== 'undefined') return stored

    const rowEl = target.closest<HTMLElement>(`[data-row-index="${row}"]`)
    return rowEl?.getBoundingClientRect().height ?? MIN_ROW_HEIGHT
  }

  // While editing a single cell, formatting should only touch that cell;
  // otherwise it applies to every cell in the current selection.
  const getFormattingTargetCellIds = (): string[] => {
    if (activatedCell) return [activatedCell]
    return Array.from(getSelectedCellIds())
  }

  const getOrCreateCell = (data: Map<string, Cell>, cellId: string): Cell => {
    let cell = data.get(cellId)
    if (!cell) {
      cell = { content: '', value: '', styling: [] }
      data.set(cellId, cell)
    }
    return cell
  }

  const iterateStyles = (styles: Style[]) => {
    if (blockedByTrial()) return
    const targetCellIds = getFormattingTargetCellIds()
    const anchorCellId = highlightedCell || activatedCell

    if (targetCellIds.length === 0 || !anchorCellId) {
      return
    }

    const newData = structuredClone(spreadsheet!.data)

    // Figure out the next size once, from the anchor cell, so the whole
    // selection moves to the same size instead of each cell cycling on its own.
    const anchorCell = getOrCreateCell(newData, anchorCellId)
    let nextSize: Style
    if (anchorCell.styling.includes('text-lg')) {
      nextSize = 'text-xl'
    } else if (anchorCell.styling.includes('text-xl')) {
      nextSize = 'text-2xl'
    } else if (anchorCell.styling.includes('text-2xl')) {
      nextSize = 'text-3xl'
    } else if (anchorCell.styling.includes('text-3xl')) {
      nextSize = 'text-base'
    } else {
      nextSize = 'text-lg'
    }

    const sizes: Style[] = ['text-base', 'text-lg', 'text-xl', 'text-2xl', 'text-3xl']
    targetCellIds.forEach((cellId) => {
      const cell = getOrCreateCell(newData, cellId)
      cell.styling = cell.styling.filter((style) => !sizes.includes(style))
      cell.styling.push(nextSize)
    })

    updateSpreadsheetTracked({
      data: newData,
    })
  }

  const toggleStyle = (style: Style, action: 'add' | 'remove' = 'add') => {
    if (blockedByTrial()) return
    const targetCellIds = getFormattingTargetCellIds()
    const anchorCellId = highlightedCell || activatedCell

    if (targetCellIds.length === 0 || !anchorCellId) {
      return
    }

    const newData = structuredClone(spreadsheet!.data)

    // Whether to add or remove is decided once from the anchor cell's current
    // state, then applied uniformly across the whole selection.
    const anchorCell = newData.get(anchorCellId)
    const shouldRemove = !!anchorCell && anchorCell.styling.indexOf(style) !== -1

    targetCellIds.forEach((cellId) => {
      const cell = getOrCreateCell(newData, cellId)

      // Remove any old bg and text styles
      ;['text-', 'bg-'].forEach((styleType) => {
        if (style.indexOf(styleType) !== -1) {
          cell.styling = cell.styling.filter((styleApplied) => {
            return styleApplied.indexOf(styleType) === -1
          })
        }
      })

      if (shouldRemove) {
        const index = cell.styling.indexOf(style)
        if (index !== -1) cell.styling.splice(index, 1)
      } else {
        if (action === 'add') cell.styling.push(style)
      }
    })

    updateSpreadsheetTracked({
      data: newData,
    })
  }

  const setCellType = (type: CellType) => {
    if (blockedByTrial()) return
    const targetCellIds = getFormattingTargetCellIds()

    if (targetCellIds.length === 0) {
      return
    }

    const newData = structuredClone(spreadsheet!.data)

    targetCellIds.forEach((cellId) => {
      const cell = getOrCreateCell(newData, cellId)
      cell.type = type
    })

    updateSpreadsheetTracked({
      data: newData,
    })
  }

  const highlightedCellColorClass = (
    type: 'bg' | 'text',
  ): Style | undefined => {
    if (!highlightedCell) return undefined

    const cell = spreadsheet!.data.get(highlightedCell)
    if (!cell) return undefined

    return cell.styling.find((style) => {
      return style.includes(type)
    })
  }

  const highlightedCellHasStyle = (style: Style): boolean => {
    if (!highlightedCell) {
      return false
    }

    const cell = spreadsheet!.data.get(highlightedCell)

    return !!cell && cell.styling.indexOf(style) !== -1
  }

  useEffect(() => {
    const handleKeyDown = (e: any) => {
      // Grid shortcuts (type-to-activate, arrow navigation, backspace-to-clear,
      // etc.) must not fire while the user is typing in a focused text field
      // like the formula bar or the in-cell textarea — those handle their own
      // keystrokes and rely on normal text-editing behavior (caret movement,
      // character deletion).
      const targetTagName = (e.target as HTMLElement | null)?.tagName
      if (targetTagName === 'INPUT' || targetTagName === 'TEXTAREA') {
        return
      }

      // A multi-cell selection is anything beyond the anchor cell itself.
      const isMultiCellSelection = getSelectedCellIds().size > 1

      const isAlphanumeric = /^[a-z0-9=]$/i.test(e.key)
      if (
        isAlphanumeric &&
        highlightedCell &&
        !isMultiCellSelection &&
        !e.metaKey &&
        !e.ctrlKey
      ) {
        // Without this, the browser's own default text-insertion for this
        // keystroke can still land in the textarea once it mounts and gets
        // focused below, on top of the character we just set via state —
        // doubling it up.
        e.preventDefault()
        activateCellWithText(highlightedCell, e.key)
      }

      if (e.key.startsWith('Arrow') && highlightedCell) {
        e.preventDefault()

        if (e.shiftKey) {
          // Extends from the current far corner, keeping the anchor fixed —
          // not from the anchor itself — so repeated presses grow the
          // selection one cell further each time.
          const { column, row } = parseCellId(selectionEnd || highlightedCell)
          let newColumn = column
          let newRow = row
          if (e.key === 'ArrowUp') newRow = Math.max(0, row - 1)
          if (e.key === 'ArrowDown') newRow = Math.min(spreadsheet!.number_of_rows - 1, row + 1)
          if (e.key === 'ArrowLeft') newColumn = Math.max(0, column - 1)
          if (e.key === 'ArrowRight') newColumn = Math.min(spreadsheet!.number_of_columns - 1, column + 1)
          const newEndCellId = getCellId(newColumn, newRow)
          setSelectionEnd(newEndCellId === highlightedCell ? '' : newEndCellId)
        } else {
          const { column, row } = parseCellId(highlightedCell)
          let newColumn = column
          let newRow = row
          if (e.key === 'ArrowUp') newRow = Math.max(0, row - 1)
          if (e.key === 'ArrowDown') newRow = Math.min(spreadsheet!.number_of_rows - 1, row + 1)
          if (e.key === 'ArrowLeft') newColumn = Math.max(0, column - 1)
          if (e.key === 'ArrowRight') newColumn = Math.min(spreadsheet!.number_of_columns - 1, column + 1)
          setHighLightedCell(getCellId(newColumn, newRow))
          setSelectionEnd('')
          setAdditionalSelectedCells(new Set())
        }
      }

      if ((e.key === 'Enter' || e.key === 'Return') && highlightedCell) {
        e.preventDefault()
        // With multiple cells marked, Enter does nothing (no single cell to edit).
        if (!isMultiCellSelection) {
          activateCell(highlightedCell)
        }
      }

      if (e.key === 'b' && (e.metaKey || e.ctrlKey) && highlightedCell) {
        e.preventDefault()
        toggleStyle('font-bold')
      }

      // Copies the computed/displayed value (e.g. a formula's result), not
      // its raw content — only for a single highlighted cell, not while it's
      // activated for editing (that's native textarea/input copy) or
      // multi-selected.
      if (
        e.key.toLowerCase() === 'c' &&
        (e.metaKey || e.ctrlKey) &&
        highlightedCell &&
        !isMultiCellSelection
      ) {
        e.preventDefault()
        navigator.clipboard.writeText(getCellValue(highlightedCell))
      }

      if ((e.key === 'Backspace' || e.key === 'Delete') && highlightedCell) {
        const newData = structuredClone(spreadsheet!.data)
        let changed = false

        getSelectedCellIds().forEach((cellId) => {
          const cell = newData.get(cellId)
          if (cell) {
            cell.content = ''
            cell.value = ''
            newData.set(cellId, cell)
            changed = true
          }
        })

        if (changed) {
          updateSpreadsheetTracked({ data: newData })
        }
      }

      if (e.key.toLowerCase() === 'z' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        if (e.shiftKey) {
          redo()
        } else {
          undo()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [highlightedCell, selectionEnd, spreadsheet, spreadsheetHistory])

  const shiftRowsInData = (
    data: Map<string, Cell>,
    fromRowNum: number, // 1-indexed: cells with rowNum >= fromRowNum shift
    delta: number, // +1 to shift down, -1 to shift up
    skipRowNum?: number, // 1-indexed row to drop entirely (for delete)
  ): Map<string, Cell> => {
    const newData = new Map<string, Cell>()
    data.forEach((cell, cellId) => {
      const match = cellId.match(/^([a-z]+)(\d+)$/)
      if (!match) {
        newData.set(cellId, cell)
        return
      }
      const colCode = match[1]
      const rowNum = parseInt(match[2])
      if (skipRowNum !== undefined && rowNum === skipRowNum) return
      if (rowNum >= fromRowNum) {
        newData.set(`${colCode}${rowNum + delta}`, cell)
      } else {
        newData.set(cellId, cell)
      }
    })
    return newData
  }

  const addRowAbove = (row: number) => {
    // row is 0-based; shift all cells at 1-indexed rowNum >= row+1 down by 1
    const newData = shiftRowsInData(spreadsheet!.data, row + 1, 1)
    updateSpreadsheetTracked({
      data: newData,
      number_of_rows: spreadsheet!.number_of_rows + 1,
    })
  }

  const addRowBelow = (row: number) => {
    // row is 0-based; shift all cells at 1-indexed rowNum >= row+2 down by 1
    const newData = shiftRowsInData(spreadsheet!.data, row + 2, 1)
    updateSpreadsheetTracked({
      data: newData,
      number_of_rows: spreadsheet!.number_of_rows + 1,
    })
  }

  const deleteRow = (row: number) => {
    if (!window.confirm(t('spreadsheet:confirmDeleteRow', { row: row + 1 }))) return
    const rowNum1Indexed = row + 1
    const newData = shiftRowsInData(spreadsheet!.data, rowNum1Indexed + 1, -1, rowNum1Indexed)
    updateSpreadsheetTracked({
      data: newData,
      number_of_rows: spreadsheet!.number_of_rows - 1,
    })
  }

  // Every cell in a SUM(XX:YY)/AVG(XX:YY) range is one group, so they all share
  // a color; each direct cell reference outside a SUM/AVG gets its own group/color.
  const referenceGroups: string[][] =
    SpreadsheetFormulaCalculator.getReferenceGroups(activatedCellContent)

  const referenceGroupsKey = referenceGroups.map((group) => group.join(',')).join('|')

  const referenceColors = useMemo(() => {
    const shuffledPalette = [...REFERENCE_COLOR_PALETTE].sort(
      () => Math.random() - 0.5,
    )
    const map = new Map<string, { bg: string; text: string }>()

    referenceGroups.forEach((group, index) => {
      const color = shuffledPalette[index % shuffledPalette.length]
      group.forEach((cellId) => map.set(cellId, color))
    })

    return map
  }, [activatedCell, referenceGroupsKey])

  // Whether clicking a cell right now should insert a reference at the
  // caret rather than highlight/activate it — true when the caret sits
  // right after an operator, an opening paren, or a range colon, e.g.
  // `=ROUND(SUM(c6:|))` (caret marked `|`), not just at the end of the formula.
  const canInsertReferenceAtCaret = (
    content: string,
    caretPosition: number | null,
  ): boolean => {
    if (!SpreadsheetFormulaCalculator.isFormula(content)) return false
    if (caretPosition === null || caretPosition === 0) return false

    const charBeforeCaret = content[caretPosition - 1]

    return '=+-/*(:'.includes(charBeforeCaret)
  }

  const selectedCellIds = getSelectedCellIds()
  const selectionLabelBounds = getOverallSelectionBounds(selectedCellIds)
  const selectionSummary = getSelectionSummary(selectedCellIds)
  const selectionSummaryContent = selectionSummary
    ? (
      <>
        {t('spreadsheet:selectionSummaryTotal', {
          total: formatCurrency(
            selectionSummary.total,
            true,
            selectionSummary.isCurrency,
          ),
        })}
        <span className='mx-1 text-gray-400'>|</span>
        {t('spreadsheet:selectionSummaryAverage', {
          average: formatCurrency(
            selectionSummary.average,
            true,
            selectionSummary.isCurrency,
          ),
        })}
      </>
    )
    : null

  return (
    <>
      <WelcomeSpreadsheet
        sheet={spreadsheet!}
        show={
          isPaymentOverdue(spreadsheet!) ||
          (!spreadsheet!.data.size &&
            !spreadsheet!.is_paid &&
            !spreadsheet!.has_accepted_payment &&
            !urlParams.get('hide'))
        }
      />
      <Paywall
        show={showTrialEndedPaywall}
        onHide={() => toggleTrialEndedPaywall(false)}
        sheetType={SheetType.SPREADSHEET}
        sheetId={spreadsheet!.id}
        name={spreadsheet!.name || t('spreadsheet:title')}
      >
        <TrialEndedPaywallContent sheet={spreadsheet!} />
      </Paywall>
      <div className='flex w-full'>
        <div className='grow'>
          <SheetTitle
            type={SheetType.SPREADSHEET}
            title={spreadsheet!.name || t('spreadsheet:title')}
            url={`/spreadsheets/${spreadsheet!.id}`}
          />

          {spreadsheet?.description && (
            <p className='italic mt-2'>{spreadsheet!.description}</p>
          )}
        </div>
        <SpreadsheetMenu />
      </div>

      <div ref={formulaBarRowRef} className='flex items-stretch gap-3 my-5'>
        <div
          ref={formattingToolbarRef}
          className='inline-block shrink-0 bg-gray-50 px-4 py-2 border'
        >
          <MdUndo
            className={`cursor-pointer inline-block p-1 ${spreadsheetHistory.canUndo ? 'text-black' : 'text-gray-300'}`}
            size='1.75rem'
            title={t('spreadsheet:undo')}
            onClick={undo}
          />
          <MdRedo
            className={`cursor-pointer inline-block p-1 ml-1 ${spreadsheetHistory.canRedo ? 'text-black' : 'text-gray-300'}`}
            size='1.75rem'
            title={t('spreadsheet:redo')}
            onClick={redo}
          />
          <span className='inline-block h-7 align-middle border-r ml-2' />
          <CellStyling
            style='font-bold'
            isActive={!!highlightedCell}
            isEnabled={highlightedCellHasStyle('font-bold')}
            toggle={() => toggleStyle('font-bold')}
          />
          <CellStyling
            style='italic'
            isActive={!!highlightedCell}
            isEnabled={highlightedCellHasStyle('italic')}
            toggle={() => toggleStyle('italic')}
          />
          <CellStyling
            style='underline'
            isActive={!!highlightedCell}
            isEnabled={highlightedCellHasStyle('underline')}
            toggle={() => toggleStyle('underline')}
          />
          <MdFormatSize
            className={`cursor-pointer inline-block p-1  ml-3 ${highlightedCell ? 'text-black' : 'text-gray-300'}`}
            size='1.75rem'
            onClick={() => {
              iterateStyles(['text-base', 'text-lg', 'text-xl', 'text-2xl'])
            }}
          />
          <CellStylingColorPicker
            type='text'
            isActive={!!highlightedCell}
            backgroundColor={highlightedCellColorClass('text')}
            toggle={(style: Style, action?: 'add' | 'remove') =>
              toggleStyle(style, action)
            }
          />
          <CellStylingColorPicker
            type='bg'
            isActive={!!highlightedCell}
            backgroundColor={highlightedCellColorClass('bg')}
            toggle={(style: Style, action?: 'add' | 'remove') =>
              toggleStyle(style, action)
            }
          />
          <span className='ml-3 pl-3 border-l'>{t('spreadsheet:cellType')}</span>
          <select
            className='ml-1 border text-sm px-1 py-1 disabled:text-gray-300 disabled:bg-gray-50'
            disabled={!highlightedCell}
            value={highlightedCell ? getCellType(highlightedCell) : 'normal'}
            onChange={(e) => setCellType(e.target.value as CellType)}
          >
            <option value='normal'>{t('spreadsheet:cellTypeNormal')}</option>
            <option value='currency'>
              {t('spreadsheet:cellTypeCurrency')}
            </option>
          </select>
        </div>
        <input
          type='text'
          className='border px-2 py-2 flex-none w-full disabled:bg-gray-100 disabled:text-gray-400'
          // flex-1's flex-basis would win over an explicit width, so the
          // fallback is a plain w-full (100% of the row's remaining space)
          // until formulaBarWidth is measured and takes over — needed since
          // this must be able to shrink below that natural width too, not
          // just grow past it.
          style={
            formulaBarWidth ? { width: `${formulaBarWidth}px` } : undefined
          }
          disabled={!formulaBarCellId}
          value={formulaBarValue}
          placeholder={t('spreadsheet:formulaBarPlaceholder')}
          onChange={handleFormulaBarChange}
          onKeyDown={handleFormulaBarKeyDown}
          onBlur={handleFormulaBarBlur}
          onFocus={() => {
            // Activate on focus (not just once typing starts) so referenced
            // cells light up as soon as the user clicks into an existing
            // formula, matching the in-grid double-click activation below.
            if (activatedCell || !highlightedCell) return
            skipTextareaAutoFocusRef.current = true
            activateCell(highlightedCell)
          }}
          onMouseDown={() => {
            // Only relevant when a textarea is actually active to defer —
            // setting it while a cell is merely highlighted would leave it
            // stuck true, wrongly deferring a later, unrelated blur-save.
            if (activatedCell) {
              formulaBarFocusPendingRef.current = true
            }
          }}
        />
      </div>
      <div
        onMouseMove={(e) => {
          if (typeof resizeConfiguration !== 'undefined') {
            setResizeConfiguration({
              ...resizeConfiguration,
              width: Math.max(
                MIN_COLUMN_WIDTH,
                resizeConfiguration.startWidth +
                  (e.clientX - resizeConfiguration.x),
              ),
            })
          }
          if (typeof rowResizeConfiguration !== 'undefined') {
            setRowResizeConfiguration({
              ...rowResizeConfiguration,
              height: Math.max(
                MIN_ROW_HEIGHT,
                rowResizeConfiguration.startHeight +
                  (e.clientY - rowResizeConfiguration.y),
              ),
            })
          }
        }}
        onMouseUp={(e) => {
          if (resizeConfiguration) {
            const newRows = structuredClone(spreadsheet!.rows)

            newRows.set(resizeConfiguration.row, {
              ...newRows.get(resizeConfiguration.row),
              width: Math.max(
                MIN_COLUMN_WIDTH,
                resizeConfiguration.startWidth +
                  (e.clientX - resizeConfiguration.x),
              ),
            })

            updateSpreadsheetTracked({ rows: newRows })

            setResizeConfiguration(undefined)
          }
          if (rowResizeConfiguration) {
            const newRows = structuredClone(spreadsheet!.rows)
            const key = String(rowResizeConfiguration.row)

            newRows.set(key, {
              ...newRows.get(key),
              height: Math.max(
                MIN_ROW_HEIGHT,
                rowResizeConfiguration.startHeight +
                  (e.clientY - rowResizeConfiguration.y),
              ),
            })

            updateSpreadsheetTracked({ rows: newRows })

            setRowResizeConfiguration(undefined)
          }
        }}
        className={`relative ${resizeConfiguration ? 'cursor-col-resize' : ''} ${rowResizeConfiguration ? 'cursor-row-resize' : ''}`}
      >
        <div className='absolute' ref={gridContentRef}>
          <div className='flex'>
            <div className='grow'>
              <div className='inline-block border text-center min-w-12 bg-gray-50 h-7'>
                <span className='opacity-0'>x</span>
              </div>
              {Array.from({ length: spreadsheet!.number_of_columns }).map(
                (_, column) => (
                  <div
                    key={`0-${column}`}
                    className='inline-block border text-center cell bg-gray-50 relative h-7'
                    style={{
                      width: `${getRowWidth(getColumnCode(column))}px`,
                    }}
                  >
                    {getColumnCode(column)}
                    <div
                      className='cursor-col-resize w-4 h-full absolute z-50 -right-2 top-0'
                      onMouseDown={(e) => {
                        setResizeConfiguration({
                          startWidth: getRowWidth(getColumnCode(column)),
                          x: e.clientX,
                          row: getColumnCode(column),
                        })
                      }}
                    ></div>
                  </div>
                ),
              )}

              {Array.from({ length: spreadsheet!.number_of_rows }).map(
                (_, row) => (
                  <div
                    key={row}
                    className='flex'
                    data-row-index={row}
                    style={
                      typeof getRowHeight(row) !== 'undefined'
                        ? { height: `${getRowHeight(row)}px` }
                        : undefined
                    }
                  >
                    <div
                      className='border text-center min-w-12 bg-gray-50 overflow-hidden relative'
                      onMouseEnter={() => setHoveredRow(row)}
                      onMouseLeave={() => setHoveredRow(null)}
                    >
                      <div
                        className='cursor-row-resize h-2 w-full absolute z-50 -bottom-1 left-0'
                        onMouseDown={(e) => {
                          e.stopPropagation()
                          setRowResizeConfiguration({
                            startHeight: measureRowHeight(
                              e.currentTarget,
                              row,
                            ),
                            y: e.clientY,
                            row,
                          })
                        }}
                      ></div>
                      {hoveredRow === row
                        ? (
                          <div className='grid grid-cols-2 w-full h-full text-xs'>
                            <div className='flex flex-col border-r border-gray-300'>
                              <div
                                className='flex-1 flex items-center justify-center border-b border-gray-300 hover:bg-gray-200 cursor-pointer'
                                onClick={(e) => { e.stopPropagation(); addRowAbove(row) }}
                                title={t('spreadsheet:insertRowAbove')}
                              >+</div>
                              <div
                                className='flex-1 flex items-center justify-center hover:bg-gray-200 cursor-pointer'
                                onClick={(e) => { e.stopPropagation(); addRowBelow(row) }}
                                title={t('spreadsheet:insertRowBelow')}
                              >+</div>
                            </div>
                            <div
                              className='flex items-center justify-center hover:bg-gray-200 cursor-pointer'
                              onClick={(e) => { e.stopPropagation(); deleteRow(row) }}
                              title={t('spreadsheet:deleteRow')}
                            >-</div>
                          </div>
                        )
                        : (
                          row + 1
                        )}
                    </div>
                    {Array.from({ length: spreadsheet!.number_of_columns }).map(
                      (_, column) => (
                        <div
                          key={`${row}-${column}`}
                          className={`border cell px-1 relative ${activatedCell === getCellId(column, row) ? 'z-[60]' : ''} ${highlightedCell === getCellId(column, row) ? 'border-theme-blue' : ''} ${selectedCellIds.size > 1 && selectedCellIds.has(getCellId(column, row)) ? 'bg-theme-blue/10' : ''}  ${cellErrors.get(getCellId(column, row)) ? 'border-red-500' : ''} ${referenceColors.get(getCellId(column, row))?.bg ?? ''} ${activatedCell === getCellId(column, row) ? '' : getCellClassNames(getCellId(column, row))}`}
                          style={{
                            width: `${getRowWidth(getColumnCode(column))}px`,
                          }}
                          onMouseDown={(e) => {
                            const cellId = getCellId(column, row)
                            const caretPosition =
                              activeTextareaRef.current?.selectionStart ?? null

                            // Runs before the textarea's blur event, so onBlur can tell a
                            // reference-insert click apart from an actual commit (Enter,
                            // clicking away) even when the caret sits after a trigger char.
                            referenceInsertPendingRef.current =
                              activatedCell !== cellId &&
                              canInsertReferenceAtCaret(
                                activatedCellContent,
                                caretPosition,
                              )

                            // Only track a potential drag-to-select when this mousedown
                            // isn't itself editing/inserting-into a formula — dragging
                            // while a cell is being edited is not supported.
                            didDragRef.current = false
                            mouseDownCellRef.current =
                              !activatedCell && !referenceInsertPendingRef.current
                                ? cellId
                                : null

                            // A plain (unmodified) click/drag start is a brand new
                            // selection — Shift/Cmd/Ctrl clicks handle their own state
                            // in onClick and must keep any cells added so far.
                            if (
                              mouseDownCellRef.current &&
                              !e.shiftKey &&
                              !e.metaKey &&
                              !e.ctrlKey
                            ) {
                              setAdditionalSelectedCells(new Set())
                            }

                            // Without this, dragging across cells to select also drags
                            // a native text selection (e.g. over the row-number gutter).
                            if (mouseDownCellRef.current) {
                              e.preventDefault()
                            }
                          }}
                          onMouseEnter={(e) => {
                            if (!mouseDownCellRef.current) return
                            if (!(e.buttons & 1)) {
                              mouseDownCellRef.current = null
                              return
                            }

                            const cellId = getCellId(column, row)
                            if (cellId !== mouseDownCellRef.current) {
                              didDragRef.current = true
                            }
                            setHighLightedCell(mouseDownCellRef.current)
                            setSelectionEnd(
                              cellId === mouseDownCellRef.current ? '' : cellId,
                            )
                          }}
                          onClick={(e) => {
                            const cellId = getCellId(column, row)

                            // A drag-select that ended back on its own start cell still
                            // fires a click there — ignore it, the selection is already set.
                            if (didDragRef.current) {
                              didDragRef.current = false
                              return
                            }

                            // Shift+click extends the selection from its anchor to the
                            // clicked cell, mirroring shift+arrow — only outside edit mode.
                            if (e.shiftKey && highlightedCell && !activatedCell) {
                              setSelectionEnd(cellId === highlightedCell ? '' : cellId)
                              return
                            }

                            // Cmd/Ctrl+click adds the clicked cell to the selection —
                            // clicking one already added this way removes it again.
                            if (
                              (e.metaKey || e.ctrlKey) &&
                              highlightedCell &&
                              !activatedCell
                            ) {
                              setAdditionalSelectedCells((prev) => {
                                const next = new Set(prev)
                                if (next.has(cellId)) {
                                  next.delete(cellId)
                                } else {
                                  next.add(cellId)
                                }
                                return next
                              })
                              return
                            }

                            // Click the same cell as the active one should do nothing
                            if (activatedCell === cellId) return

                            // if a cell is currently being edited with a formula, and the caret sits right
                            // after an operator/paren/colon, insert the clicked cell as a reference there
                            const caretPosition =
                              activeTextareaRef.current?.selectionStart ?? null

                            if (
                              activatedCell &&
                              canInsertReferenceAtCaret(
                                activatedCellContent,
                                caretPosition,
                              )
                            ) {
                              const insertAt = caretPosition as number
                              const newContent =
                                activatedCellContent.slice(0, insertAt) +
                                cellId +
                                activatedCellContent.slice(insertAt)

                              setActivatedCellContent(newContent)

                              requestAnimationFrame(() => {
                                const el = activeTextareaRef.current
                                if (!el) return
                                el.focus()
                                const newCaretPosition = insertAt + cellId.length
                                el.setSelectionRange(newCaretPosition, newCaretPosition)
                              })

                              return
                            }

                            // Single click will highlight, anything else will activate
                            if (e.detail === 1) {
                              highlightCell(cellId)
                            } else {
                              activateCell(cellId)
                            }
                          }}
                        >
                          <div
                            className='cursor-col-resize w-2 h-full absolute z-40 -right-1 top-0'
                            onMouseDown={(e) => {
                              e.stopPropagation()
                              setResizeConfiguration({
                                startWidth: getRowWidth(getColumnCode(column)),
                                x: e.clientX,
                                row: getColumnCode(column),
                              })
                            }}
                          ></div>
                          <div
                            className='cursor-row-resize h-2 w-full absolute z-40 -bottom-1 left-0'
                            onMouseDown={(e) => {
                              e.stopPropagation()
                              setRowResizeConfiguration({
                                startHeight: measureRowHeight(
                                  e.currentTarget,
                                  row,
                                ),
                                y: e.clientY,
                                row,
                              })
                            }}
                          ></div>
                          {activatedCell === getCellId(column, row) && (
                            <div className='relative'>
                              <div
                                aria-hidden
                                ref={activeOverlayRef}
                                className='absolute left-0 top-0 whitespace-pre pointer-events-none'
                              >
                                {renderHighlightedFormula(
                                  activatedCellContent,
                                  referenceColors,
                                )}
                              </div>
                              <textarea
                                value={activatedCellContent}
                                ref={activeTextareaRef}
                                onChange={(e) =>
                                  setActivatedCellContent(e.target.value)
                                }
                                // A textarea's CSS width doesn't grow with its value (its
                                // max-content size comes from the `cols` attribute, not the
                                // text), so the native caret drifts out of sync with the
                                // overlay above once content is longer than that default —
                                // width is driven instead by the overlay's measured width,
                                // so it always extends at least as far as the real text does.
                                style={{
                                  width: `${Math.max(
                                    getRowWidth(getColumnCode(column)),
                                    activatedCellTextWidth + 8,
                                  )}px`,
                                }}
                                className='relative border-0 outline-none m-0 p-0 bg-transparent text-transparent caret-black whitespace-pre'
                                onBlur={() => {
                                  if (escapeCancelPendingRef.current) {
                                    escapeCancelPendingRef.current = false
                                    return
                                  }

                                  const deferSave =
                                    referenceInsertPendingRef.current ||
                                    formulaBarFocusPendingRef.current
                                  referenceInsertPendingRef.current = false
                                  formulaBarFocusPendingRef.current = false

                                  if (!deferSave) {
                                    saveCellContent(getCellId(column, row))
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    const belowCellId = getCellId(
                                      column,
                                      Math.min(
                                        spreadsheet!.number_of_rows - 1,
                                        row + 1,
                                      ),
                                    )
                                    // blur() triggers onBlur -> saveCellContent synchronously above;
                                    // highlighting the cell below afterwards lands after that save.
                                    e.currentTarget.blur()
                                    setHighLightedCell(belowCellId)
                                  }

                                  if (e.key === 'Escape') {
                                    e.preventDefault()
                                    // Cancel the edit (discard unsaved changes) and fall back
                                    // to just highlighting the cell instead of committing it.
                                    escapeCancelPendingRef.current = true
                                    setActivatedCellContent('')
                                    setActivatedCell('')
                                    setHighLightedCell(getCellId(column, row))
                                    e.currentTarget.blur()
                                  }

                                  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                    const targetCellId = getCellId(
                                      column,
                                      e.key === 'ArrowUp'
                                        ? Math.max(0, row - 1)
                                        : Math.min(
                                            spreadsheet!.number_of_rows - 1,
                                            row + 1,
                                        ),
                                    )
                                    // blur() triggers onBlur -> saveCellContent synchronously above;
                                    // highlighting the target cell afterwards lands after that save.
                                    e.currentTarget.blur()
                                    setHighLightedCell(targetCellId)
                                  }
                                }}
                              />
                            </div>
                          )}

                          {activatedCell !== getCellId(column, row) && (
                            <div
                              className={`relative ${cellErrors.get(getCellId(column, row)) ? 'pt-1' : ''}`}
                            >
                              <div className='overflow-hidden whitespace-nowrap'>
                                <span>
                                  {getCellValue(getCellId(column, row))}
                                </span>
                              </div>
                              {cellErrors.get(getCellId(column, row)) && (
                                <span className='absolute -top-2 bg-white left-0 text-red-500 text-xs'>
                                  {cellErrors.get(getCellId(column, row))}
                                </span>
                              )}
                            </div>
                          )}

                          {selectionSummaryContent &&
                            selectionLabelBounds &&
                            column === selectionLabelBounds.maxColumn &&
                            row === selectionLabelBounds.maxRow && (
                            <div className='absolute left-full bottom-0 ml-1 whitespace-nowrap rounded border bg-white px-2 py-1 text-xs text-black shadow z-[70] pointer-events-none'>
                              {selectionSummaryContent}
                            </div>
                          )}
                        </div>
                      ),
                    )}
                  </div>
                ),
              )}
            </div>
            <div
              className='bg-gray-50 text-gray-400 border cursor-pointer px-2 flex items-center'
              onClick={() => {
                updateSpreadsheetTracked({
                  number_of_columns: spreadsheet!.number_of_columns + 1,
                })
              }}
            >
              <span>+</span>
            </div>
          </div>
          <div
            className='bg-gray-50 text-center text-gray-400 border cursor-pointer'
            onClick={() =>
              updateSpreadsheetTracked({
                number_of_rows: spreadsheet!.number_of_rows + 1,
              })
            }
          >
            +
          </div>
        </div>
      </div>
    </>
  )
}
